import { inject, Injectable } from '@angular/core';
import { PortalFieldConfig } from '@portal/form/data-access';
import { BeneficialOwnerModelKeys, BusinessAccountModelKeys } from '../const/form-field-key.const';
import { PERSONNEL_OTHER } from '../const/app.const';
import { BeneficialOwnerFormConfigService } from './beneficial-owner-form-config.service';
import { combineLatest, debounceTime, EMPTY, map, startWith, tap } from 'rxjs';
import { BeneficialOwnerModel } from '../model';
import { FinancialRequiredStore } from '../store';

@Injectable({ providedIn: 'root' })
export class BusinessAccountFormConfigService {
  private readonly financialRequiredStore = inject(FinancialRequiredStore);
  private readonly beneficialOwnerFormConfigService = inject(BeneficialOwnerFormConfigService);

  initBusinessAccountFormFields(): PortalFieldConfig[] {
    return [
      {
        key: BusinessAccountModelKeys.accountOpeningNotRequired,
        type: '#yesNo',
        defaultValue: true,
        props: {
          label: 'Do you have a CIMB Corporate Account?',
          required: true,
        },
      },
      {
        expressions: {
          hide: field => field.model?.[BusinessAccountModelKeys.accountOpeningNotRequired] === false,
        },
        template: '<div><b>Note:</b> Your account will only be used/opened upon acceptance of Letter of offer for CIMB SME Loan.</div>',
      },
      {
        expressions: {
          hide: field => field.model?.[BusinessAccountModelKeys.accountOpeningNotRequired] === true,
        },
        fieldGroup: [
          {
            template: `<div class='font-bold text-xl my-4'>For New Business Accounts</div>`,
          },
          {
            key: BusinessAccountModelKeys.notAutoRegistration,
            type: 'checkbox-boolean',
            props: {
              label: 'Auto-Registration For CIMB PayNow Service',
              labelCheckbox: 'I/We would like to opt out of the auto-registration for the PayNow Service for the Account(s)',
            },
          },
          {
            template: `<div class='font-bold portal-paragraph'>Corporate E-Alerts Service</div>
                      <div>I/We would like to receive notification for transaction amount that hits: </div>
            `,
          },
          {
            key: BusinessAccountModelKeys.minimumAmountReceiveNotification,
            type: '#minimumAmountReceiveNotification',
          },
          {
            key: BusinessAccountModelKeys.notUseCorporateEAlertsService,
            type: 'checkbox-boolean',
            props: {
              wrapperClass: '!mb-0',
              labelCheckbox:
                'I/We would like to opt out from Corporate E-Alerts Service and I/we will monitor transactions without E-Alerts notifications via email and/or SMS.',
            },
          },
          {
            template: `
              <div class="mt-2 portal-paragraph"><span class='font-bold'>Note:</span> Charges may apply. Refer to Corporate Pricing Guide for details.</div>
              <div class="mt-4 md:mt-6 portal-paragraph font-bold">Let us know your Company Ultimate Beneficial Owner(s). We recommend to include guarantors/shareholders with more than 25% ownership or equivalent.</div>
            `,
          },
          {
            key: BusinessAccountModelKeys.beneficialOwners,
            type: 'form-array-beneficial-owner',
            fieldArray: {
              fieldGroupClassName: 'field-columns',
              fieldGroup: this.beneficialOwnerFormConfigService.initBeneficialOwnerFormFields(),
            },
            validators: { validation: ['uniqueNricInArray'] },
            hooks: {
              onInit: ({ formControl }: PortalFieldConfig) => {
                const portalBeneficialOwnerOptionsMap$ = this.beneficialOwnerFormConfigService.portalBeneficialOwnerOptionsMap$;
                const formControlChanges$ = formControl?.valueChanges.pipe(startWith(formControl?.value)) || EMPTY;
                const portalPersonnelOptionsMap$ = this.financialRequiredStore.portalPersonnelOptionsMap$;
                const disableSelectedOptions = (uuids: string[]) => {
                  const boOptions = Array.from(portalBeneficialOwnerOptionsMap$.getValue().values()).map(bo => {
                    bo.disabled = bo.value !== PERSONNEL_OTHER && uuids.includes(bo.value);
                    return bo;
                  });
                  this.beneficialOwnerFormConfigService.portalBeneficialOwnerOptions$.next(boOptions);
                };

                return combineLatest([formControlChanges$, portalBeneficialOwnerOptionsMap$, portalPersonnelOptionsMap$]).pipe(
                  map(() =>
                    Array.isArray(formControl?.getRawValue())
                      ? formControl
                          ?.getRawValue()
                          ?.map((v: BeneficialOwnerModel) => v?.[BeneficialOwnerModelKeys.personnel_uuid])
                          .filter((v: BeneficialOwnerModel) => !!v)
                      : [],
                  ),
                  debounceTime(300),
                  tap((uuids: string[]) => disableSelectedOptions(uuids)),
                );
              },
            },
          },
          {
            expressions: {
              hide: field => field.model?.[BusinessAccountModelKeys.accountOpeningNotRequired] === true,
            },
            template: `
              <div class="text-[#9A9A9A] mt-2 portal-paragraph">By proceeding,</div>
              <div class="text-[#9A9A9A] mt-2 portal-paragraph">I/We declare that the above individual(s) ultimately own(s) or has/have effective control over the Account (“Ultimate Beneficial Owner(s)”).</div>
              <div class="text-[#9A9A9A] mt-2 portal-paragraph">I/We acknowledge and confirm that the Bank and its officers/employees/agents or persons acting on behalf of the Bank shall be entitled to rely on the identity(ies) of and information relating to the Beneficial Owner(s) of this Account.</div>
            `,
          },
        ],
      },
    ];
  }
}
